import React, { useRef, useState, FC, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';

export const StyledAccordionWrapper = styled.div<{
  isOpenAccordion: boolean;
  hasError: boolean;
  customizations: AccordionCustomization;
  isPlaceholder?: boolean;
}>`
  && {
    max-width: 800px;
    width: 100%;
    border: ${({ hasError, isOpenAccordion }) =>
      !isOpenAccordion && hasError ? '1px solid #6367ef' : ''};
    border-radius: 5px;
    overflow: hidden;
    flex-grow: 1;

    & .accordion {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.6rem 13px;
      cursor: pointer;
      background: ${({ customizations }) => customizations.accordionBg};

      & .title-wrapper {
        display: flex;
        flex-direction: column;
        word-break: break-word;
        & .title {
          opacity: ${({ isPlaceholder }) => (isPlaceholder ? '0.5' : '1')};
        }
      }

      & .accordion__icon {
        user-select: none;
        display: flex;
        margin-left: 10px;
      }
    }
    & .content-wrapper {
      transition: height 0.35s ease-out;
      height: ${({ isOpenAccordion }) => (isOpenAccordion ? `auto` : '0')};
      :focus-within {
        height: auto;
      }
      background: ${({ customizations }) => customizations.contentBg};
      & .content {
        padding: ${({ customizations }) =>
          customizations.padding ?? '0.6rem 8px'};
      }
    }
  }
`;

export interface AccordionCustomization {
  padding: string;
  contentBg: string;
  accordionBg: string;
}

interface AccordionProps {
  title: string;
  hasError: boolean;
  children: React.ReactNode;
  customizations: AccordionCustomization;
}

export const default_customizations: AccordionCustomization = {
  padding: `0.6rem 8px`,
  contentBg: '#F1F3F9',
  accordionBg: '#F1F3F9',
};

export const Accordion = ({
  title,
  hasError,
  children,
  customizations,
  maxWidth = '800px',
  isPlaceholder = false,
}: {
  title;
  hasError;
  children;
  customizations;
  maxWidth?;
  isPlaceholder?: boolean;
}) => {
  const mergedCustomization = {
    ...default_customizations,
    ...customizations,
  };

  const contentRef = useRef(null);

  const [isOpenAccordion, setIsOpenAccordion] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const toggleAccordion = () => setIsOpenAccordion((prevState) => !prevState);
  const onContentHandler = (e) => e.stopPropagation();

  useEffect(() => {
    setContentHeight(contentRef.current.offsetHeight);
  }, []);

  return (
    <StyledAccordionWrapper
      onClick={toggleAccordion}
      isOpenAccordion={isOpenAccordion}
      contentHeight={contentHeight}
      customizations={mergedCustomization}
      hasError={hasError}
      style={{ maxWidth }}
      isPlaceholder={isPlaceholder}
    >
      <div className="accordion">
        <div className="title-wrapper">
          <div className="title">{title}</div>
        </div>
        <div className="accordion__icon">
          {isOpenAccordion ? (
            <FontAwesomeIcon icon={faMinus as any} />
          ) : (
            <FontAwesomeIcon icon={faPlus as any} />
          )}
        </div>
      </div>

      <div className="content-wrapper">
        <div className="content" ref={contentRef} onClick={onContentHandler}>
          {children}
        </div>
      </div>
    </StyledAccordionWrapper>
  );
};
